import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DSBadge, DSIcon } from '../design-system/primitives';
import { getCategoryLabel, isEventCancelled, isEventInProgress, isEventOver } from '../utils/eventHelpers';
import pingolfHero from '../assets/pingolf-hero.webp';
import { apiAssetUrl } from '../utils/apiAssetUrl';
import './EventsBrowse.css';

const DAY = 86400000;

const CATEGORY_ORDER = ['pinball', 'video_game', 'board_game', 'book_club', 'other'];

export const SCOPES = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'mine', label: 'My RSVPs' },
  { key: 'past', label: 'Past' }
];

/**
 * Two letters for an event with no artwork. Built from the first two words that start with
 * a letter or digit, so "New England Pinball League" reads NE and "Queer Pinball" reads QP.
 */
export const monogram = (title) => String(title || '')
  .split(' ')
  .filter((word) => /^[A-Za-z0-9]/.test(word))
  .slice(0, 2)
  .map((word) => word[0].toUpperCase())
  .join('');

/**
 * The artwork for this event. A photo uploaded from the event's edit form wins. After that,
 * the artwork the website already shows: a one-off event can carry its own URL in metadata,
 * and everything generated from a series inherits the series image. PinGolf nights are
 * created straight from the league (no series behind them), so they fall back to the same
 * hero banner the PinGolf pages already use rather than showing a monogram.
 */
export const eventImage = (event) =>
  apiAssetUrl(event.photo_url)
  || event.event_metadata?.featured_image_url
  || event.series_image_url
  || (event.event_metadata?.pinball_format === 'pingolf' ? pingolfHero : null);

const dayLabel = (date) => date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const timeLabel = (date) => date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
export const dateLine = (date) => `${dayLabel(date)} · ${timeLabel(date)}`;

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * Monday of the week `date` falls in, at midnight. The buckets below are calendar weeks
 * rather than rolling seven day windows, so "next week" means the week after this one
 * rather than "eight to fourteen days away".
 */
const startOfWeek = (date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
};

/**
 * Groups the list into This week / Next week / Later. Past events all land in one bucket,
 * because "the week before last" is not a useful thing to tell someone.
 */
export const bucketEvents = (items, now = new Date(), isPast = false) => {
  const weekStart = startOfWeek(now);
  const nextWeek = weekStart.getTime() + 7 * DAY;
  const laterStart = weekStart.getTime() + 14 * DAY;

  const buckets = [
    { key: 'this', label: 'This week', items: [] },
    { key: 'next', label: 'Next week', items: [] },
    { key: 'later', label: isPast ? 'Earlier' : 'Later', items: [] }
  ];

  items.forEach((item) => {
    const time = item.date.getTime();
    const index = isPast ? 2 : time < nextWeek ? 0 : time < laterStart ? 1 : 2;
    buckets[index].items.push(item);
  });

  return buckets.filter((bucket) => bucket.items.length > 0);
};

// A reason longer than this reads as a paragraph on a list row. The event page shows it whole.
const SHORT_REASON_LENGTH = 80;

function EventRow({ event, showThumb, onOpen }) {
  return (
    <button
      type="button"
      className={`eb-row${event.cancelled ? ' eb-row--cancelled' : ''}`}
      onClick={() => onOpen(event.id)}
    >
      {showThumb && (
        <span className="eb-row__thumb" aria-hidden="true">
          {event.image
            ? <img className="eb-row__img" src={event.image} alt="" loading="lazy" />
            : <span className="eb-row__mono">{event.mono}</span>}
        </span>
      )}
      <span className="eb-row__body">
        <span className="eb-row__topline">
          <span className="eb-row__date">{event.dateLine}</span>
          {event.cancelled ? (
            <DSBadge variant="error" size="sm" uppercase className="eb-cancelled-badge">Cancelled</DSBadge>
          ) : (
            <>
              {event.live && <span className="eb-pill eb-pill--live">Happening now</span>}
              {event.going && <span className="eb-pill eb-pill--going">Going</span>}
              {event.hosting && <span className="eb-pill eb-pill--hosting">Hosting</span>}
            </>
          )}
        </span>
        <span className="eb-row__title">{event.title}</span>
        {event.cancelled && event.cancellationReason && event.cancellationReason.length <= SHORT_REASON_LENGTH && (
          <span className="eb-row__cancelnote">{event.cancellationReason}</span>
        )}
        {!event.cancelled && event.description && <span className="eb-row__desc">{event.description}</span>}
        {event.host && (
          <span className="eb-row__host">
            <DSIcon name="user" size="sm" />
            {event.host}
          </span>
        )}
      </span>
    </button>
  );
}

function HeroCard({ event, onOpen }) {
  return (
    <button type="button" className="eb-hero" onClick={() => onOpen(event.id)}>
      <span className="eb-hero__card">
        <span className="eb-hero__art">
          {event.image
            ? <img className="eb-hero__img" src={event.image} alt="" />
            : <span className="eb-hero__mono" aria-hidden="true">{event.mono}</span>}
          <span className="eb-hero__scrim" aria-hidden="true" />
          <span className="eb-hero__chip">{event.whenChip}</span>
          <span className="eb-hero__caption">
            <span className="eb-hero__cat">{event.catLabel}</span>
            <span className="eb-hero__title">{event.title}</span>
          </span>
        </span>
        <span className="eb-hero__meta">
          <span className="eb-hero__when">
            <span className="eb-hero__whenIcon"><DSIcon name="clock" size="sm" /></span>
            {event.dateLine}
            {event.going && <span className="eb-pill eb-pill--going">Going</span>}
            {event.hosting && <span className="eb-pill eb-pill--hosting">Hosting</span>}
            {!event.going && !event.hosting && <span className="eb-pill eb-pill--none">Not RSVP'd</span>}
          </span>
          {event.description && <span className="eb-hero__desc">{event.description}</span>}
          <span className="eb-hero__foot">
            <DSIcon name="user" size="sm" />
            {event.host ? `Host ${event.host}` : 'Host to be confirmed'}
            <span className="eb-hero__spacer" />
            <span className="eb-hero__details">Details &rsaquo;</span>
          </span>
        </span>
      </span>
    </button>
  );
}

/**
 * The events list: scope tabs, category chips with counts, a hero card for the next thing
 * happening, and the rest grouped by week. Every card opens the event page - there is no
 * intermediate detail modal, because the modal only ever repeated what the page says.
 */
function EventsBrowse({
  events = [],
  myRsvps = [],
  currentUser,
  scope = 'upcoming',
  onScopeChange,
  categoryFilter = 'all',
  onCategoryChange,
  onOpenEvent,
  showHero = true,
  showThumbnails = true,
  groupByDate = true
}) {
  const now = useMemo(() => new Date(), []);

  const decorated = useMemo(() => {
    const rsvpByEvent = new Map(myRsvps.map((rsvp) => [rsvp.event_id, rsvp]));

    return events
      .filter((event) => event.is_active !== false)
      .map((event) => {
        const date = new Date(event.event_date);
        const rsvp = rsvpByEvent.get(event.id);
        const status = String(rsvp?.status || '').trim().toLowerCase();
        const hosting = Boolean(event.hosts?.some((host) => host.id === currentUser?.id));

        return {
          id: event.id,
          date,
          // Settled against the one `now` this render holds, so a list can never show the
          // same event as both under way and finished.
          live: isEventInProgress(event, now.getTime()),
          over: isEventOver(event, now.getTime()),
          // Stays in the list on purpose, so people who planned on it see the news.
          cancelled: isEventCancelled(event),
          cancellationReason: event.cancellation_reason || '',
          title: event.title,
          cat: event.event_category || 'other',
          catLabel: getCategoryLabel(event.event_category),
          host: event.hosts?.map((host) => host.first_name).join(', ') || '',
          description: event.description || '',
          mono: monogram(event.title),
          image: eventImage(event),
          dateLine: dateLine(date),
          going: status === 'going',
          // "Mine" is anything they are on the hook for: hosting it, or having said they
          // are coming. A not_going RSVP is a decision not to be there, so it is not.
          mine: hosting || (status !== '' && status !== 'not_going'),
          hosting
        };
      });
  }, [events, myRsvps, currentUser, now]);

  const isPast = scope === 'past';

  const [scopeOpen, setScopeOpen] = useState(false);
  const scopeRef = useRef(null);
  const scopeLabel = (SCOPES.find((item) => item.key === scope) || SCOPES[0]).label;

  // The menu overlaps the list, so a tap outside it should dismiss rather than fall through
  // to whichever event happens to be underneath.
  useEffect(() => {
    if (!scopeOpen) return undefined;
    const onDocDown = (e) => {
      if (scopeRef.current && !scopeRef.current.contains(e.target)) setScopeOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setScopeOpen(false); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [scopeOpen]);

  const scoped = useMemo(() => {
    // An event that has started is still the one people need: the host checks players in
    // from it and the room looks up what is on tonight. It moves to Past when it is over,
    // not the moment it begins.
    const list = decorated.filter((event) => (isPast ? event.over : !event.over));
    const filtered = scope === 'mine' ? list.filter((event) => event.mine) : list;
    return filtered.sort((a, b) => (isPast ? b.date - a.date : a.date - b.date));
  }, [decorated, isPast, scope]);

  const chips = useMemo(() => {
    const counts = { all: scoped.length };
    scoped.forEach((event) => {
      counts[event.cat] = (counts[event.cat] || 0) + 1;
    });

    return ['all', ...CATEGORY_ORDER.filter((key) => counts[key])].map((key) => ({
      key,
      label: key === 'all' ? 'All types' : getCategoryLabel(key),
      count: counts[key] || 0
    }));
  }, [scoped]);

  const visible = useMemo(
    () => scoped.filter((event) => categoryFilter === 'all' || event.cat === categoryFilter),
    [scoped, categoryFilter]
  );

  // "Next up" means the next thing actually happening, so a cancelled night never takes the
  // hero slot. It stays in the rows below with its Cancelled badge.
  const heroEvent = showHero && !isPast ? visible.find((event) => !event.cancelled) || null : null;
  const rows = heroEvent ? visible.filter((event) => event !== heroEvent) : visible;

  const groups = useMemo(() => {
    if (!groupByDate) {
      return rows.length
        ? [{ key: 'all', label: isPast ? 'Past events' : 'All dates', items: rows }]
        : [];
    }
    return bucketEvents(rows, now, isPast);
  }, [rows, groupByDate, isPast, now]);

  const hero = heroEvent && {
    ...heroEvent,
    whenChip: heroEvent.live
      ? 'Happening now'
      : heroEvent.date.getTime() - now.getTime() < DAY * 2 ? 'Next up · soon' : 'Next up'
  };

  return (
    <div className="eb">
      <div className="eb-sticky">
        <div className="eb-titlerow">
          <h1 className="eb-title">Events</h1>
          <span className="eb-count">{plural(visible.length, 'event')}</span>
        </div>

        {/* Scope collapses to one pill so it can share a row with the type chips, per the
            design. Three always-visible tabs on their own line cost a whole row to show two
            options nobody is on, and the chips already scroll sideways beside it. */}
        <div className="eb-filters" ref={scopeRef}>
          <button
            type="button"
            className="eb-scopebtn"
            aria-haspopup="listbox"
            aria-expanded={scopeOpen}
            onClick={() => setScopeOpen((open) => !open)}
          >
            {scopeLabel}
            <DSIcon name="chevronDown" size="sm" />
          </button>

          <span className="eb-filters__divider" aria-hidden="true" />

          <div className="eb-chips noscroll">
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                aria-pressed={categoryFilter === chip.key}
                className={`eb-chip${categoryFilter === chip.key ? ' is-active' : ''}`}
                onClick={() => { setScopeOpen(false); onCategoryChange(chip.key); }}
              >
                {chip.label}
                <span className="eb-chip__count">{chip.count}</span>
              </button>
            ))}
          </div>

          {scopeOpen && (
            <div className="eb-scopemenu" role="listbox" aria-label="Which events">
              {SCOPES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="option"
                  aria-selected={scope === item.key}
                  className={`eb-scopemenu__item${scope === item.key ? ' is-active' : ''}`}
                  onClick={() => { setScopeOpen(false); onScopeChange(item.key); }}
                >
                  {item.label}
                  {scope === item.key && <span aria-hidden="true">&#10003;</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {hero && <HeroCard event={hero} onOpen={onOpenEvent} />}

      <div className="eb-groups">
        {groups.map((group) => (
          <section key={group.key || group.label}>
            <div className="eb-grouphead">
              <h2 className="eb-grouphead__label">{group.label}</h2>
              <span className="eb-grouphead__rule" />
              <span className="eb-grouphead__count">{plural(group.items.length, 'event')}</span>
            </div>
            <div className="eb-grouplist">
              {group.items.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  showThumb={showThumbnails}
                  onOpen={onOpenEvent}
                />
              ))}
            </div>
          </section>
        ))}

        {visible.length === 0 && (
          <div className="eb-empty">
            <p className="eb-empty__title">Nothing here yet</p>
            <p className="eb-empty__note">
              {scope === 'mine'
                ? "You have not RSVP'd to anything upcoming."
                : 'No events match these filters.'}
            </p>
            <button
              type="button"
              className="eb-empty__reset"
              onClick={() => {
                onScopeChange('upcoming');
                onCategoryChange('all');
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventsBrowse;
